import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Download, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet, Wrench, Sparkles, Plus, ListFilter, Database, Boxes, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, fetchOrphanCalibers, createMissingMovements, standardizeMovementTypes, enrichMovementsFromWatches } from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const EXPORT_URL = `${process.env.REACT_APP_BACKEND_URL}/api/data/export.xlsx`;

export default function Data() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  // Reconcile report state
  const [orphans, setOrphans] = useState(null); // { total, items[] } | null
  const [reconcileBusy, setReconcileBusy] = useState(false);

  const loadOrphans = async () => {
    setReconcileBusy(true);
    try {
      const data = await fetchOrphanCalibers();
      setOrphans(data);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load report"));
    } finally {
      setReconcileBusy(false);
    }
  };

  useEffect(() => { loadOrphans(); }, []);

  const createAllMissing = async () => {
    setReconcileBusy(true);
    try {
      toast.info("Creating movements and enriching with AI — this may take a minute…");
      const res = await createMissingMovements(true, null);
      toast.success(
        `Created ${res.created} movement${res.created === 1 ? "" : "s"} · AI-enriched ${res.enriched}` +
        (res.errors?.length ? ` · ${res.errors.length} error(s)` : "")
      );
      await loadOrphans();
    } catch (err) {
      toast.error(errorMessage(err, "Create-missing failed"));
    } finally {
      setReconcileBusy(false);
    }
  };

  const createOne = async (caliber) => {
    setReconcileBusy(true);
    try {
      const res = await createMissingMovements(true, [caliber]);
      if (res.created > 0) {
        toast.success(`Created "${caliber}"${res.enriched ? " + AI enriched" : ""}`);
      } else {
        toast.info(`"${caliber}" was already a movement`);
      }
      await loadOrphans();
    } catch (err) {
      toast.error(errorMessage(err, "Create failed"));
    } finally {
      setReconcileBusy(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setSummary(null);
  };

  const exportAll = async () => {
    // Server sets Content-Disposition; let the browser take it.
    try {
      const r = await api.get("/data/export.xlsx", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "tickdoc-export.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Workbook downloaded");
    } catch (err) {
      toast.error(errorMessage(err, "Export failed"));
    }
  };

  const preview = async () => {
    if (!file) return;
    setBusy(true); setSummary(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/data/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSummary(data.summary);
    } catch (err) {
      toast.error(errorMessage(err, "Preview failed"));
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file || !summary) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirm", "yes");
      const { data } = await api.post("/data/import/commit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported · ${data.touched_watches} watches touched`);
      setSummary(null);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(errorMessage(err, "Import failed"));
    } finally { setBusy(false); }
  };

  const repairIds = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/watches/repair-ids");
      if (data.repaired === 0) {
        toast.success("No watches needed repair");
      } else {
        toast.success(`Repaired ${data.repaired} watch${data.repaired === 1 ? "" : "es"} — IDs reassigned`);
      }
    } catch (err) {
      toast.error(errorMessage(err, "Repair failed"));
    } finally { setBusy(false); }
  };

  const runStandardize = async () => {
    setBusy(true);
    try {
      const r = await standardizeMovementTypes("Auto", "Automatic");
      toast.success(`Renamed Auto → Automatic on ${r.watches_updated} watches, ${r.movements_updated} movements`);
    } catch (err) {
      toast.error(errorMessage(err, "Standardize failed"));
    } finally { setBusy(false); }
  };

  const runEnrichFromWatches = async () => {
    setBusy(true);
    try {
      toast.info("Backfilling movement specs from your watch records…");
      const r = await enrichMovementsFromWatches(false);
      const f = r.fields_filled || {};
      toast.success(
        `Touched ${r.movements_touched} movements · ` +
        `+${f.movement_type || 0} types, +${f.jewels || 0} jewels, ` +
        `+${f.bph || 0} bph, +${f.mvmnt_mfg || 0} mfg, ` +
        `+${f.protection || 0} shock, +${f.lift_angle || 0} lift_angle defaults`,
        { duration: 8000 },
      );
    } catch (err) {
      toast.error(errorMessage(err, "Enrichment failed"));
    } finally { setBusy(false); }
  };

  const totals = summary && Object.values(summary).reduce(
    (acc, s) => ({ n: acc.n + s.new, u: acc.u + s.update, e: acc.e + s.errors.length }),
    { n: 0, u: 0, e: 0 },
  );

  return (
    <div className="px-6 md:px-10 py-8 pb-28 max-w-5xl">
      <header className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-2">Data</div>
        <h1 className="font-serif-display text-4xl text-[#0A0604] leading-none">Import &amp; Export</h1>
        <p className="text-sm text-[#3A2A10] mt-3 max-w-xl">
          Round-trip the whole workshop to a single Excel workbook. Export, edit
          off-line, then upload to see a diff before anything is written.
        </p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Download size={16} className="text-[#3A2A10]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Export</div>
          </div>
          <h2 className="font-serif-display text-2xl text-[#0A0604] mb-2">One workbook · 5 sheets</h2>
          <p className="text-sm text-[#3A2A10] mb-5">
            Watches · Movements · Suppliers · Parts · Services. All current rows, ready
            to edit in Excel or Google Sheets.
          </p>
          <Button
            onClick={exportAll}
            className="h-10 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm"
            data-testid="export-xlsx-btn"
          >
            <FileSpreadsheet size={14} className="mr-1.5" /> Download .xlsx
          </Button>
        </div>

        <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Upload size={16} className="text-[#3A2A10]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Import</div>
          </div>
          <h2 className="font-serif-display text-2xl text-[#0A0604] mb-2">Upload &amp; preview</h2>
          <p className="text-sm text-[#3A2A10] mb-5">
            Pick a workbook — you&rsquo;ll see exactly what changes before anything is saved.
            Watches match by <code className="font-mono-data text-[#0A0604]">excel_id</code>,
            movements by caliber, suppliers by name.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            onChange={onPick}
            className="block text-xs text-[#3A2A10] mb-3 file:mr-3 file:h-9 file:px-3 file:border file:border-[#8B6A1F]/45 file:bg-transparent file:text-[#3A2A10] file:uppercase file:tracking-wider file:text-[10px] file:rounded-sm file:cursor-pointer"
            data-testid="import-file-input"
          />
          <Button
            onClick={preview}
            disabled={!file || busy}
            className="h-10 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm disabled:opacity-40"
            data-testid="import-preview-btn"
          >
            {busy && !summary ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
            Preview changes
          </Button>
        </div>
      </section>

      {summary && (
        <section className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-6" data-testid="import-summary">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Preview</div>
              <h3 className="font-serif-display text-2xl text-[#0A0604]">
                {totals.n} new · {totals.u} updated
                {totals.e > 0 && <span className="text-rose-600"> · {totals.e} error{totals.e === 1 ? "" : "s"}</span>}
              </h3>
            </div>
            <Button
              onClick={commit}
              disabled={busy || totals.e > 0 && (totals.n + totals.u) === 0}
              className="h-10 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm disabled:opacity-40"
              data-testid="import-commit-btn"
            >
              {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
              Apply Import
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {Object.entries(summary).map(([sheet, s]) => (
              <div key={sheet} className="border border-[#8B6A1F]/30 bg-[#FBF3D9] p-4">
                <div className="font-serif-display text-lg text-[#0A0604]">{sheet}</div>
                <div className="flex items-center gap-3 mt-2 text-sm font-mono-data">
                  <span className="text-emerald-700">+{s.new}</span>
                  <span className="text-[#3A2A10]">~{s.update}</span>
                  {s.errors.length > 0 && (
                    <span className="text-rose-600 inline-flex items-center gap-1">
                      <AlertTriangle size={12} /> {s.errors.length}
                    </span>
                  )}
                </div>
                {s.errors.length > 0 && (
                  <ul className="mt-2 text-[10px] text-rose-600 list-disc pl-4 space-y-0.5 max-h-32 overflow-y-auto">
                    {s.errors.slice(0, 20).map((err) => <li key={err}>{err}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-6 mt-8" data-testid="maintenance-section">
        <div className="flex items-center gap-2 mb-3">
          <Wrench size={16} className="text-[#3A2A10]" />
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Maintenance</div>
        </div>
        <h2 className="font-serif-display text-2xl text-[#0A0604] mb-2">Repair watch IDs</h2>
        <p className="text-sm text-[#3A2A10] mb-5 max-w-xl">
          Assigns the next available <code className="font-mono-data text-[#0A0604]">excel_id</code> to
          any watch that is currently missing one. Run this before re-importing your
          spreadsheet so matches work correctly and no duplicates are created.
        </p>
        <Button
          onClick={repairIds}
          disabled={busy}
          className="h-10 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm disabled:opacity-40"
          data-testid="repair-ids-btn"
        >
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Wrench size={14} className="mr-1.5" />}
          Repair missing IDs
        </Button>
      </section>

      <section className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-6 mt-8" data-testid="reconcile-section">
        <div className="flex items-center gap-2 mb-3">
          <Wrench size={16} className="text-[#3A2A10]" />
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Reconcile</div>
        </div>
        <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h2 className="font-serif-display text-2xl text-[#0A0604] mb-1">Calibers not in Movements table</h2>
            <p className="text-sm text-[#3A2A10] max-w-xl">
              Every watch caliber that doesn&rsquo;t have a row in the Movements reference library. Create them so your inventory is fully reconciled and dropdowns stay clean.
            </p>
          </div>
          {orphans && orphans.total > 0 && (
            <Button
              onClick={createAllMissing}
              disabled={reconcileBusy}
              className="h-10 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm disabled:opacity-40"
              data-testid="reconcile-create-all"
            >
              {reconcileBusy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />}
              Create all {orphans.total} + AI enrich
            </Button>
          )}
        </div>

        {orphans === null ? (
          <div className="text-sm text-[#3A2A10]">Loading…</div>
        ) : orphans.total === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50/40 border border-emerald-500/30 rounded-sm px-3 py-2">
            <CheckCircle2 size={14} /> All calibers in your inventory are tracked in Movements.
          </div>
        ) : (
          <div className="border border-[#8B6A1F]/30 max-h-[360px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F9F0D4] border-b border-[#8B6A1F]/25">
                <tr className="text-[10px] uppercase tracking-wider text-[#3A2A10]">
                  <th className="px-3 py-2 text-left">Caliber</th>
                  <th className="px-3 py-2 text-left">Mfg (hint)</th>
                  <th className="px-3 py-2 text-right">Watches</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#8B6A1F]/15">
                {orphans.items.map((o) => (
                  <tr key={o.caliber} data-testid={`orphan-row-${o.caliber}`}>
                    <td className="px-3 py-2 font-mono-data text-[#0A0604]">{o.caliber}</td>
                    <td className="px-3 py-2 text-[#3A2A10]">{o.mvmnt_mfg || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data text-[#3A2A10]">{o.count}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => createOne(o.caliber)}
                        disabled={reconcileBusy}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#3A2A10] hover:text-[#0A0604] border border-[#8B6A1F]/40 rounded-sm px-2 py-1 disabled:opacity-40"
                        data-testid={`orphan-create-${o.caliber}`}
                      >
                        <Plus size={10} /> Create
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Maintenance utilities row */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Link
          to="/data/validation"
          className="group bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5 hover:bg-[#F5E9C5] transition-colors"
          data-testid="card-validation-manager"
        >
          <div className="flex items-center gap-2 mb-2">
            <ListFilter size={16} className="text-[#3A2A10]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Dropdowns</div>
          </div>
          <h3 className="font-serif-display text-xl text-[#0A0604] mb-2 group-hover:underline">
            Manage dropdown values
          </h3>
          <p className="text-xs text-[#3A2A10]">
            Add, rename, hide, or delete values used across the app — historical
            records stay intact.
          </p>
        </Link>

        <Link
          to="/data/inspection"
          className="group bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5 hover:bg-[#F5E9C5] transition-colors"
          data-testid="card-inspection-setup"
        >
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList size={16} className="text-[#3A2A10]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Inspection</div>
          </div>
          <h3 className="font-serif-display text-xl text-[#0A0604] mb-2 group-hover:underline">
            Inspection categories &amp; actions
          </h3>
          <p className="text-xs text-[#3A2A10]">
            Manage what you inspect (Crystal, Mainspring…) and the result/action
            options. Renames propagate to every saved inspection instantly.
          </p>
        </Link>

        <Link
          to="/data/stock-parts"
          className="group bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5 hover:bg-[#F5E9C5] transition-colors"
          data-testid="card-stock-parts"
        >
          <div className="flex items-center gap-2 mb-2">
            <Boxes size={16} className="text-[#3A2A10]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Catalog</div>
          </div>
          <h3 className="font-serif-display text-xl text-[#0A0604] mb-2 group-hover:underline">
            Stock parts &amp; prices
          </h3>
          <p className="text-xs text-[#3A2A10]">
            Crystals, mainsprings, straps — set default costs and pick from the
            list when logging service parts.
          </p>
        </Link>

        <button
          onClick={runStandardize}
          disabled={busy}
          className="text-left bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5 hover:bg-[#F5E9C5] transition-colors disabled:opacity-50"
          data-testid="btn-standardize-auto"
        >
          <div className="flex items-center gap-2 mb-2">
            <Wrench size={16} className="text-[#3A2A10]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">One-shot</div>
          </div>
          <h3 className="font-serif-display text-xl text-[#0A0604] mb-2">
            Rename Auto → Automatic
          </h3>
          <p className="text-xs text-[#3A2A10]">
            Standardize legacy "Auto" movement_type values across watches and the
            movements table. Idempotent.
          </p>
        </button>

        <button
          onClick={runEnrichFromWatches}
          disabled={busy}
          className="text-left bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5 hover:bg-[#F5E9C5] transition-colors disabled:opacity-50"
          data-testid="btn-enrich-from-watches"
        >
          <div className="flex items-center gap-2 mb-2">
            <Database size={16} className="text-[#3A2A10]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">One-shot</div>
          </div>
          <h3 className="font-serif-display text-xl text-[#0A0604] mb-2">
            Backfill movement specs
          </h3>
          <p className="text-xs text-[#3A2A10]">
            Copy mfg / type / jewels / bph / shock from existing watches into
            blank movement records. Default lift angle 52°. No AI.
          </p>
        </button>
      </section>
    </div>
  );
}
